// <instruction-md>: the root element (RFC-0025 §3–5). Loads a document from
// its light DOM, `src` or a registry `ref`, projects it once per input change,
// and renders one of three views. Deterministic given (text, options), so
// renderToString produces the same markup as the browser.
import { LitElement, html, isServer, nothing, type PropertyValues, type TemplateResult } from "lit";
import type { Parameter } from "@instruction-md/spec";
import { project, type RenderMeta, type RenderModel, type RenderOptions, type View } from "../project";
import { readInstruction } from "../registry-client";
import { layout, styles } from "../styles";
import { renderBackMatter, renderDelivered, renderHeader, renderNodes, renderSideToc, renderSource, renderToc } from "../templates";
import { enhanceDiagrams } from "../diagrams";
import { enhanceLayouts } from "../layouts";
import { resolveAnchor } from "../outline";

export const DEFAULT_REGISTRY = "https://instruction.md/mcp";
const VIEWS: View[] = ["rendered", "delivered", "source"];

function dedent(text: string): string {
  const lines = text.replace(/^\n+/, "").replace(/\s+$/, "").split("\n");
  const indents = lines.filter((l) => l.trim()).map((l) => /^[ \t]*/.exec(l)![0].length);
  const cut = indents.length ? Math.min(...indents) : 0;
  return lines.map((l) => l.slice(cut)).join("\n") + "\n";
}

function originAllowed(url: string, allow: string, registry: string): boolean {
  let u: URL;
  try { u = new URL(url, isServer ? "http://localhost/" : location.href); } catch { return false; }
  if (!isServer && u.origin === location.origin) return true;
  const list = allow.split(",").map((s) => s.trim()).filter(Boolean);
  if (list.includes("*")) return true;
  const hosts = new Set([...list, "instruction.md", "www.instruction.md"]);
  try { hosts.add(new URL(registry).host); } catch { /* ignore */ }
  return [...hosts].some((h) => u.host === h || u.host.endsWith(`.${h}`));
}

export class InstructionMd extends LitElement {
  static styles = [styles, layout];
  static properties = {
    src: { type: String },
    ref: { type: String },
    registry: { type: String },
    view: { type: String, reflect: true },
    views: { type: String },
    chrome: { type: String, reflect: true },
    backMatter: { type: String, attribute: "back-matter" },
    toc: { type: String, reflect: true },
    theme: { type: String, reflect: true },
    allowImages: { type: Boolean, attribute: "allow-images" },
    allowIncludes: { type: Boolean, attribute: "allow-includes" },
    allowOrigins: { type: String, attribute: "allow-origins" },
    maxBytes: { type: Number, attribute: "max-bytes" },
    headingOffset: { type: Number, attribute: "heading-offset" },
    params: { type: String },
    inputs: { type: Boolean, reflect: true },
    saveInputs: { type: Boolean, attribute: "save-inputs" },
    text: { attribute: false },
    meta: { attribute: false },
    integrations: { type: Object },
    _model: { state: true },
    _error: { state: true },
    _loading: { state: true },
    _activeView: { state: true },
    _values: { state: true },
  };

  declare src?: string;
  declare ref?: string;
  /** The host's approved integrations (§17): the targets of its catalog that this document names, and whether
   *  connected objects are restricted to them. As an attribute, JSON. */
  declare integrations?: RenderOptions["integrations"];
  declare registry: string;
  declare view: View;
  declare views: string;
  /** `full` (header, toggle, TOC) or `bare` — the host supplies its own chrome and drives `view`. */
  declare chrome: "full" | "bare";
  /** The back matter (rules at a glance, guardrails, figures): `on`, `off`, or unset — shown with the full chrome. */
  declare backMatter?: "on" | "off";
  /**
   * The table of contents: `side` (a rail beside the text when the element is
   * wide enough, the agenda above it when not), `agenda` (above the text only)
   * or `none`. Default: `side` with the full chrome, `none` when bare. "Wide
   * enough" is 62rem: the element then carries the `wide` attribute.
   */
  declare toc?: "side" | "agenda" | "none";
  declare theme: "light" | "dark" | "auto";
  declare allowImages: boolean;
  declare allowIncludes: boolean;
  declare allowOrigins: string;
  declare maxBytes: number;
  /** Shift the document's headings down when the host page owns the outline. */
  declare headingOffset: number;
  declare params?: string;
  /** The document's parameters as inputs above it: a value tried there is drawn where the parameter is used and goes
   *  into the delivered view, and the text stays as written (an `imd-params` event says what was tried). */
  declare inputs: boolean;
  /** With `inputs`: a Save button that hands what was tried to the host as the new defaults (`imd-params-save`). */
  declare saveInputs: boolean;
  declare text?: string;
  declare meta?: RenderMeta;
  declare _model: RenderModel | null;
  declare _error: string | null;
  declare _loading: boolean;
  declare _activeView: View | null;
  /** What the inputs hold, as typed (a cleared one leaves: its default applies). */
  declare _values: Record<string, string | boolean>;
  private _loadToken = 0;

  constructor() {
    super();
    this.registry = DEFAULT_REGISTRY;
    this.view = "rendered";
    this.views = "rendered,delivered,source";
    this.chrome = "full";
    this.theme = "auto";
    this.allowImages = false;
    this.allowIncludes = false;
    this.allowOrigins = "";
    this.maxBytes = 1_048_576;
    this.headingOffset = 0;
    this._model = null;
    this._error = null;
    this._loading = false;
    this._activeView = null;
    this.inputs = false;
    this.saveInputs = false;
    this._values = {};
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (isServer) return;
    if (!this.text && !this.src && !this.ref) {
      const inline = this.textContent ?? "";
      if (inline.trim()) this.text = dedent(inline);
    }
    if (typeof ResizeObserver !== "undefined") (this.sizer ??= new ResizeObserver(this.onResize)).observe(this);
    // moved or reconnected: no update follows, so the rail's tracking is re-armed here
    if (this.hasUpdated) this.spy();
  }

  // ---- `wide`: room for the rail beside the text (the layout sheet keys off it) ----
  // Measured with a ResizeObserver rather than a container query: inline-size
  // containment sizes the element as if empty, so in a flex row or any other
  // shrink-to-fit box it collapsed to 0px wide.
  private sizer?: ResizeObserver;
  private onResize = (entries: ResizeObserverEntry[]): void => {
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const w = entries[entries.length - 1].contentRect.width;
    // on at 62rem, off below 60rem: showing the rail can add or remove a scrollbar,
    // which moves the width back across a single threshold every frame
    const wide = this.hasAttribute("wide") ? w >= 60 * rem : w >= 62 * rem;
    // next frame: the height the change causes would re-enter this observer in the current one
    if (wide !== this.hasAttribute("wide")) requestAnimationFrame(() => this.toggleAttribute("wide", wide));
  };

  /** The model for the current text and options (also used by renderToString). */
  get model(): RenderModel | null { return this._model; }
  get activeView(): View { return this._activeView ?? this.view ?? "rendered"; }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has("text") || changed.has("params") || changed.has("allowImages") || changed.has("meta") || changed.has("inputs") || changed.has("_values") || changed.has("integrations")) this.recompute();
  }

  protected updated(changed: PropertyValues): void {
    if ((changed.has("src") || changed.has("ref") || changed.has("registry")) && !isServer) void this.load();
    if (!isServer && this.shadowRoot) { enhanceDiagrams(this.shadowRoot); enhanceLayouts(this.shadowRoot); }
    if (!isServer) this.spy();
  }

  // ---- "On this page": the heading the reader is in, marked in the rail ----------
  private spying = false;
  private spy = (): void => {
    const root = this.shadowRoot;
    const links = root ? [...root.querySelectorAll<HTMLAnchorElement>(".side-toc a[data-anchor]")] : [];
    if (!links.length) return;
    if (!this.spying) { this.spying = true; document.addEventListener("scroll", this.onScroll, { capture: true, passive: true }); window.addEventListener("resize", this.onScroll, { passive: true }); }
    this.onScroll();
  };
  private frame = 0;
  private onScroll = (): void => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const root = this.shadowRoot;
      if (!root) return;
      const links = [...root.querySelectorAll<HTMLAnchorElement>(".side-toc a[data-anchor]")];
      let current = links[0]?.dataset.anchor;
      for (const a of links) {
        const h = root.getElementById(a.dataset.anchor!);
        if (h && h.getBoundingClientRect().top < 120) current = a.dataset.anchor;
      }
      for (const a of links) a.toggleAttribute("aria-current", a.dataset.anchor === current);
    });
  };

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.sizer?.disconnect();
    if (this.spying) { document.removeEventListener("scroll", this.onScroll, { capture: true }); window.removeEventListener("resize", this.onScroll); this.spying = false; }
  }

  private parseJson(s: string | undefined, what: string): Record<string, unknown> | undefined {
    if (!s) return undefined;
    try {
      const v: unknown = JSON.parse(s);
      if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
      this._error = `${what} is a JSON object`;
    } catch { this._error = `${what} is not valid JSON`; }
    return undefined;
  }

  private recompute(): void {
    if (this.text === undefined || this.text === null) { this._model = null; return; }
    try {
      this._error = null;
      const params = this.parseJson(this.params, "params");
      this._model = project(this.text, { params: this.inputs ? { ...params, ...this._values } : params, showValues: this.inputs, allowImages: this.allowImages, headingOffset: this.headingOffset, meta: this.meta, integrations: this.integrations });
    } catch (e) {
      this._model = null;
      this._error = e instanceof Error ? e.message : String(e);
    }
  }

  private async load(): Promise<void> {
    const token = ++this._loadToken;
    this._error = null;
    if (this.ref) {
      this._loading = true;
      try {
        const r = await readInstruction(this.registry, this.ref, { maxBytes: this.maxBytes });
        if (token !== this._loadToken) return;
        this.meta = r.meta;
        this.text = r.text;
      } catch (e) { if (token === this._loadToken) this._error = e instanceof Error ? e.message : String(e); }
      finally { if (token === this._loadToken) this._loading = false; }
      return;
    }
    if (this.src) {
      if (!originAllowed(this.src, this.allowOrigins, this.registry)) { this._error = `refusing to fetch ${this.src}: origin not allowed (set allow-origins)`; return; }
      this._loading = true;
      try {
        const res = await fetch(this.src, { credentials: "omit" });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const len = Number(res.headers.get("content-length") ?? 0);
        if (len > this.maxBytes) throw new Error(`document exceeds max-bytes (${this.maxBytes})`);
        const text = await res.text();
        if (token !== this._loadToken) return;
        if (text.length > this.maxBytes) throw new Error(`document exceeds max-bytes (${this.maxBytes})`);
        this.meta = undefined;
        this.text = text;
      } catch (e) { if (token === this._loadToken) this._error = e instanceof Error ? e.message : String(e); }
      finally { if (token === this._loadToken) this._loading = false; }
    }
  }

  /** Programmatic view switch (also wired to the toggle). */
  setView(v: View): void { if (VIEWS.includes(v)) { this._activeView = v; this.dispatchEvent(new CustomEvent("imd-view", { detail: { view: v }, bubbles: true, composed: true })); } }

  private onAnchorClick(e: Event): void {
    const a = (e.target as HTMLElement).closest?.("a[href^='#']") as HTMLAnchorElement | null;
    if (!a || !this.shadowRoot) return;
    const id = decodeURIComponent(a.getAttribute("href")!.slice(1));
    const target = this.shadowRoot.querySelector(`[id="${CSS.escape(id)}"]`) ?? resolveAnchor(this.shadowRoot, id);
    if (target) { e.preventDefault(); target.scrollIntoView({ behavior: "smooth", block: "start" }); (target as HTMLElement).focus?.(); }
  }

  private async copySource(): Promise<void> {
    if (!this._model || isServer) return;
    try { await navigator.clipboard.writeText(this._model.source); this.dispatchEvent(new CustomEvent("imd-copied", { bubbles: true, composed: true })); } catch { /* clipboard unavailable */ }
  }

  // ---- the inputs: each parameter's value to try, as its type asks for it ----
  private setValue(name: string, v: string | boolean): void {
    const next = { ...this._values };
    if (v === "") delete next[name]; else next[name] = v;
    this._values = next;
    this.dispatchEvent(new CustomEvent("imd-params", { detail: { params: next }, bubbles: true, composed: true }));
  }

  private saveValues = (): void => {
    const params = this._values;
    this._values = {};
    this.dispatchEvent(new CustomEvent("imd-params-save", { detail: { params }, bubbles: true, composed: true }));
  };

  private renderInput(p: Parameter): TemplateResult {
    const tried = this._values[p.name];
    const v = tried ?? p.default ?? p.example;
    const text = v === undefined ? "" : Array.isArray(v) ? v.join(", ") : String(v);
    const typed = (e: Event) => this.setValue(p.name, (e.target as HTMLInputElement).value);
    const control = p.type === "boolean"
      ? html`<input type="checkbox" .checked=${v === true || v === "true"} @change=${(e: Event) => this.setValue(p.name, (e.target as HTMLInputElement).checked)} />`
      : p.values?.length
        ? html`<select @change=${typed}>${p.values.map((x) => html`<option .selected=${x === text}>${x}</option>`)}</select>`
        : html`<input type=${p.type === "number" || p.type === "integer" ? "number" : "text"} step=${p.type === "integer" ? "1" : "any"} .value=${text} @input=${typed} />`;
    return html`<label class="input" part="input"><span><span>${p.description ?? p.name}</span><code>\${${p.name}}</code></span>${control}</label>`;
  }

  private renderInputs(model: RenderModel): TemplateResult | typeof nothing {
    if (!this.inputs || !model.parameters.length) return nothing;
    return html`<div class="inputs" part="inputs" role="group" aria-label="Parameters">
      <p><strong>Parameters</strong> <span>${this.saveInputs ? "Change a value and save it into the instruction, or just try it here." : "Try other values: the instruction shows them, and its text stays as written."}</span>${Object.keys(this._values).length ? html`<button type="button" @click=${() => { this._values = {}; }}>Reset</button>${this.saveInputs ? html`<button type="button" class="save" @click=${this.saveValues}>Save as defaults</button>` : nothing}` : nothing}</p>
      <div>${model.parameters.map((p) => this.renderInput(p))}</div>
    </div>`;
  }

  private renderPage(model: NonNullable<RenderModel>): TemplateResult {
    const toc = this.toc || (this.chrome === "bare" ? "none" : "side");
    const back = this.backMatter === "on" || (this.backMatter !== "off" && this.chrome !== "bare") ? renderBackMatter(model) : nothing;
    const main = html`${toc !== "none" ? renderToc(model) : nothing}<div part="body" class="body">${renderNodes(model.nodes)}</div>${back}`;
    // a grid only when there is a rail to put beside the text
    const side = toc === "side" ? renderSideToc(model) : nothing;
    return side === nothing ? main : html`<div class="page has-side"><div class="page-main">${main}</div>${side}</div>`;
  }

  protected render(): TemplateResult {
    const views = this.views.split(",").map((s) => s.trim()).filter((v): v is View => VIEWS.includes(v as View));
    const active = views.includes(this.activeView) ? this.activeView : (views[0] ?? "rendered");
    const model = this._model;
    const errors = model ? model.diagnostics.filter((d) => d.severity === "error") : [];
    return html`<div class="root" part="root" @click=${this.onAnchorClick}>
      ${this._error ? html`<div class="error" part="error" role="alert">${this._error}</div>` : nothing}
      ${this._loading && !model ? html`<p class="note" aria-busy="true">Loading…</p>` : nothing}
      ${model ? html`
        ${this.chrome === "bare" ? nothing : renderHeader(model)}
        ${this.chrome !== "bare" && views.length > 1 ? html`<div class="toggle" part="toggle" role="tablist" aria-label="view">${views.map((v) => html`<button role="tab" aria-selected=${active === v ? "true" : "false"} @click=${() => this.setView(v)}>${v}</button>`)}</div>` : nothing}
        ${errors.length ? html`<p class="refusals" part="refusals">${errors.length} error${errors.length === 1 ? "" : "s"}: ${errors.map((d) => `${d.range ? `line ${d.range.start} — ` : ""}${d.message}`).join("; ")}</p>` : nothing}
        ${active !== "source" ? this.renderInputs(model) : nothing}
        ${active === "rendered" ? this.renderPage(model) : nothing}
        ${active === "delivered" ? renderDelivered(model) : nothing}
        ${active === "source" ? html`${isServer ? nothing : html`<button class="copy" part="copy" @click=${this.copySource}>copy source</button>`}${renderSource(model)}` : nothing}
      ` : nothing}
    </div>`;
  }
}
